/**
 * Lokalhyresavtal — Version 2026 (Formulär R-02).
 *
 * One block per numbered section of the legacy template. Every § is
 * auto-numbered (none hide, so the sequence is stable 1–32); sub-clauses
 * nest as sections and renumber when an optional one (15.3 biutrymmen,
 * 16.3 byggvarudeklaration, 22.2 oförutsedda kostnader) drops out.
 *
 * Generic by construction: every printed party/property datum is a slot
 * value (legacy variable key) carrying an autofill binding; the compile
 * context is consulted only for structure (attachments, signature roster).
 *
 * Legal text verbatim from the Haymanot-approved Word templates
 * (underhållsklausul.docx, Taxebundna tillägg.docx, Brandskyddsklausul.docx,
 * Fastighetsskatteklausul.docx, Ombyggnads- och ändringsarbeten +
 * skyltning.docx) and the legacy lokalhyra STATIC_CLAUSES. DO NOT paraphrase.
 */
import { attachmentList, details, em, group, list, nodes, option, optionList, p, section, strong, t, table, val, } from '@hyranai/regta-document-ir';
import { annexOrBlank, annexOrRule, contextAttachments, hasAttachment } from '../common/attachments.js';
import { fieldRows, referenceLine } from '../common/fields.js';
import { signingBlockDefinition } from '../common/signing.js';
import { formatSlotNumber, numVal, partySlot, selectSlot, shown, textVal, ticked, toSlotOptions, whenOption, withAutofill, } from '../common/slots.js';
import { arr, rows, str } from '../lib.js';
import { optionOf } from '../bostadshyra/kit.js';
import { amountVal, attachmentSlot, freeTextParagraphs, inlineSlot, leadP, sub } from './helpers.js';
import { BEVAKNING_OPTIONS, BRANDSKYDD_OPTIONS, DRIFT_MEDIA_OPTIONS, DRIFT_TABLE_POSTS, EL_MODE_OPTIONS, LANDLORD_MOMS_OPTIONS, LOCK_OPTIONS, MAINTENANCE_ANCILLARY_CLAUSES, MAINTENANCE_ANCILLARY_SELECT, MAINTENANCE_COMMON_CLAUSES, MAINTENANCE_COMMON_SELECT, MAINTENANCE_PREMISES_CLAUSES, MAINTENANCE_PREMISES_SELECT, PAYMENT_FREQUENCY_OPTIONS, PAYMENT_TYPE_OPTIONS, POSSESSION_OPTIONS, POSSESSION_WAIVERS, PREMISES_CONDITION_OPTIONS, PROPERTY_TAX_OPTIONS, RENT_ANNEX, RENT_FIXED, RENT_INCREASE_OPTIONS, RENT_KPI, SECURITY_TYPE_OPTIONS, SEDVANLIGT_UNDERHALL_CLAUSES, SEDVANLIGT_UNDERHALL_OPTIONS, SHARE_CALC_OPTIONS, SIZE_DESCRIPTION_OPTIONS, TENANT_MOMS_OPTIONS, UNFORESEEN_OPTIONS, UPPRAKNINGS_MODELL_OPTIONS, WASTE_TRANSPORT_OPTIONS, driftMediaLabel, optionValue, } from './options.js';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: 'Lokalhyresavtal Version 2026 (Haymanot-approved Word templates)' },
};
const SHARED = { plane: 'shared', status: 'published', language: 'sv-SE' };
const row = (term, value) => [
    term,
    [p(Array.isArray(value) ? value : [value])],
];
const singleChoice = (items, sourceKey) => optionList(items, { select: 'single', ...(sourceKey ? { sourceKey } : {}) });
/* ------------------------------------------------------------------ */
/* Avtalsuppgifter + parter                                            */
/* ------------------------------------------------------------------ */
export const avtalsuppgifter = {
    ...SHARED,
    blockKey: 'lokalhyra.avtalsuppgifter',
    version: 'v1',
    title: 'Avtalsuppgifter',
    summary: 'Contract and object reference numbers printed above the preamble.',
    slots: [
        { key: 'contract_number', label: 'Avtalsnummer', type: 'text', required: true, help: 'Egen referens på avtalet, t.ex. A-2042.' },
        { key: 'object_number', label: 'Objektsnummer', type: 'text', help: 'Valfritt — egen referens på hyresobjektet.' },
    ],
    compile: (input, ctx) => nodes(referenceLine([
        ['Avtalsnummer', textVal(input, 'contract_number')],
        shown(ctx, !!str(input.object_number)) && ['Objektsnummer', textVal(input, 'object_number')],
    ])),
};
const PARTY_BINDING = {
    landlord: { role: 'lessor', ordinal: 0 },
    tenant: { role: 'lessee', ordinal: 0 },
    cotenant: { role: 'lessee', ordinal: 1 },
};
/** Name / org.nr / gatuadress / postnummeradress — the ADR-0065 split-address projection. */
function partySlots(prefix, required, suffix = '') {
    const { role, ordinal } = PARTY_BINDING[prefix];
    const optional = required ? '' : 'Valfritt — ';
    return [
        partySlot(`${prefix}_name`, `Namn${suffix}`, role, ordinal, 'name', {
            required,
            help: `${optional}${required ? 'F' : 'f'}ullständigt namn eller företagsnamn`,
        }),
        partySlot(`${prefix}_org_number`, `Organisationsnummer${suffix}`, role, ordinal, 'legalId', { required, help: 'XXXXXX-XXXX' }),
        partySlot(`${prefix}_address`, `Gatuadress${suffix}`, role, ordinal, 'street', { required }),
        partySlot(`${prefix}_postal_address`, `Postnummeradress${suffix}`, role, ordinal, 'postalAddress', { required, help: '123 45 Ort' }),
    ];
}
function partyRows(input, prefix) {
    return nodes(fieldRows([
        ['Namn', [textVal(input, `${prefix}_name`)]],
        ['Org.nr', [textVal(input, `${prefix}_org_number`)]],
        ['Gatuadress', [textVal(input, `${prefix}_address`)]],
        ['Postnummer', [textVal(input, `${prefix}_postal_address`)]],
    ]));
}
const COTENANT_KEYS = ['cotenant_name', 'cotenant_org_number', 'cotenant_address', 'cotenant_postal_address'];
const hasCotenant = (input) => COTENANT_KEYS.some((key) => !!input[key]);
export const hyresvard = {
    ...SHARED,
    blockKey: 'lokalhyra.hyresvard',
    version: 'v1',
    title: 'Hyresvärd',
    summary: 'The landlord’s name, organisation number and split postal address.',
    slots: partySlots('landlord', true),
    compile: (input) => [section('hyresvard', 'Hyresvärd', partyRows(input, 'landlord'), { keepTogether: true })],
};
export const hyresgast = {
    ...SHARED,
    blockKey: 'lokalhyra.hyresgast',
    version: 'v1',
    title: 'Hyresgäst',
    summary: 'The tenant’s details, plus an optional co-tenant (medhyresgäst) shown once any of its fields is filled.',
    slots: [...partySlots('tenant', true), ...partySlots('cotenant', false, ' (medhyresgäst)')],
    compile: (input, ctx) => [
        section('hyresgast', 'Hyresgäst', nodes(...partyRows(input, 'tenant'), shown(ctx, hasCotenant(input)) ? group(partyRows(input, 'cotenant'), { title: 'Medhyresgäst' }) : null), { keepTogether: true, ...(input.beside_previous === true ? { sideBySide: true } : {}) }),
    ],
};
/* ------------------------------------------------------------------ */
/* § Hyresobjekt                                                       */
/* ------------------------------------------------------------------ */
const appurtenanceArea = (raw) => {
    if (raw == null)
        return null;
    const text = String(raw).replace(/[^\S\n]+/g, ' ').trim();
    if (text === '')
        return null;
    return /^[\d\s.,]+$/.test(text) ? `${text} kvm` : text;
};
export const hyresobjekt = {
    ...LEGAL,
    blockKey: 'lokalhyra.hyresobjekt',
    version: 'v1',
    title: 'Hyresobjekt',
    summary: 'The let premises: property, address, floor, size (explicit area or per attached drawing), appurtenant spaces and the area-deviation clause.',
    slots: [
        withAutofill({ key: 'property_designation', label: 'Fastighetsbeteckning', type: 'text', required: true }, { kind: 'property', field: 'designation' }),
        withAutofill({ key: 'property_address', label: 'Lokalens adress', type: 'textarea', required: true }, { kind: 'property', field: 'address' }),
        withAutofill({ key: 'property_kommun', label: 'Kommun', type: 'text' }, { kind: 'property', field: 'municipality' }),
        { key: 'property_floor', label: 'Våningsplan', type: 'text', help: 'T.ex. 2 tr' },
        selectSlot('size_description_mode', 'Beskrivning av lokalens storlek', SIZE_DESCRIPTION_OPTIONS, { required: true, default: 'explicit_area' }),
        attachmentSlot('size_drawing_annex', 'Bilaga – handlingar', {
            required: true,
            repeats: true,
            visibleWhen: { key: 'size_description_mode', equals: 'per_drawing' },
            help: 'Välj eller ladda upp en eller flera handlingar som visar lokalens storlek och omfattning.',
        }),
        {
            key: 'premises_area',
            label: 'Yta (m²)',
            type: 'number',
            help: 'I läget "Hänvisa till bilagd ritning" är yta valfri och skrivs ut endast om den anges.',
        },
        {
            key: 'premises_appurtenances',
            label: 'Tillhörande utrymmen',
            type: 'table',
            repeats: true,
            help: 'Lista tillhörande biutrymmen med yta, t.ex. förråd eller parkeringsplats.',
            columns: [
                { key: 'name', label: 'Utrymme', type: 'text' },
                { key: 'area', label: 'Yta', type: 'text' },
            ],
        },
        {
            key: 'include_planlosning',
            default: true,
            label: 'Bifoga planlösning',
            type: 'boolean',
            help: 'Bilaga 1 reserveras för planlösningen — ladda upp ritningen nedan. Avmarkera om ingen planlösningsbilaga ska bifogas.',
        },
    ],
    compile: (input, ctx) => {
        const perDrawing = input.size_description_mode === 'per_drawing';
        const showArea = ctx.mode === 'blank' || !perDrawing || typeof input.premises_area === 'number';
        const entered = rows(input.premises_appurtenances);
        const appurtenances = entered.length === 0 && ctx.mode === 'blank' ? [{ name: '', area: '' }] : entered;
        return {
            attachments: contextAttachments(ctx),
            nodes: [
                section('hyresobjekt', 'Hyresobjekt', nodes(details([
                    row('Fastighetsbeteckning', textVal(input, 'property_designation')),
                    row('Lokalens adress', textVal(input, 'property_address', 'long')),
                    row('Kommun', textVal(input, 'property_kommun')),
                    row('Våningsplan', textVal(input, 'property_floor', 'short')),
                    ...(showArea ? [row('Yta (m²)', numVal(ctx, input, 'premises_area'))] : []),
                ]), shown(ctx, perDrawing)
                    ? p([
                        t('Lokalens storlek och omfattning framgår av ovan angivna uppgifter och bilagd(a) handling(-ar). '),
                        ...annexOrBlank(ctx, input.size_drawing_annex, 'size_drawing_annex'),
                        t('.'),
                    ])
                    : null, shown(ctx, entered.length > 0)
                    ? group([
                        table([
                            { key: 'name', header: [t('Utrymme')], width: 75 },
                            { key: 'area', header: [t('Yta')], width: 25 },
                        ], appurtenances.map((r) => [
                            [p([val(str(r.name), { blankWidth: 'long', sourceKey: 'premises_appurtenances' })])],
                            [p([val(appurtenanceArea(r.area), { blankWidth: 'short', sourceKey: 'premises_appurtenances' })])],
                        ]), { variant: 'data' }),
                    ], { title: 'Tillhörande utrymmen' })
                    : null, leadP('Areaavvikelse.', [
                    ' Om i avtalet angiven area avviker från faktisk area medför avvikelsen inte rätt för Hyresgästen till återbetalning eller sänkning av hyran respektive Hyresvärden rätt till högre hyra.',
                ]))),
            ],
        };
    },
};
/* ------------------------------------------------------------------ */
/* §§ Hyrestid, användning, skick                                      */
/* ------------------------------------------------------------------ */
export const hyrestid = {
    ...LEGAL,
    blockKey: 'lokalhyra.hyrestid-uppsagning',
    version: 'v1',
    title: 'Hyrestid och uppsägning',
    summary: 'Lease term, mutual notice period and automatic extension, plus the written-notice requirement of 12 kap. jordabalken.',
    slots: [
        { key: 'lease_start_date', label: 'Startdatum', type: 'date', required: true },
        { key: 'lease_end_date', label: 'Slutdatum', type: 'date', required: true },
        { key: 'notice_months', label: 'Ömsesidig uppsägningstid (månader)', type: 'number', required: true, help: 'T.ex. 9' },
        { key: 'extension_months', label: 'Förlängning om ej uppsagt (månader)', type: 'number', required: true, help: 'T.ex. 12' },
    ],
    compile: (input, ctx) => [
        section('hyrestid-uppsagning', 'Hyrestid och uppsägning', [
            details([
                row('Startdatum', val(str(input.lease_start_date), { format: 'date', sourceKey: 'lease_start_date' })),
                row('Slutdatum', val(str(input.lease_end_date), { format: 'date', sourceKey: 'lease_end_date' })),
                row('Ömsesidig uppsägningstid (månader)', numVal(ctx, input, 'notice_months')),
                row('Förlängning om ej uppsagt (månader)', numVal(ctx, input, 'extension_months')),
            ], { columns: 2 }),
            p('Uppsägning av avtalet ska vara skriftlig. Parterna ska iaktta övriga formkrav som följer av 12 kap. jordabalken.'),
        ]),
    ],
};
export const anvandning = {
    ...LEGAL,
    blockKey: 'lokalhyra.anvandning',
    version: 'v1',
    title: 'Lokalens användning',
    summary: 'The permitted use of the premises, as free text.',
    slots: [
        {
            key: 'premises_usage',
            label: 'Lokalens ändamål',
            type: 'textarea',
            required: true,
            help: 'Beskriv verksamhetens art, t.ex. butikslokal för detaljhandel',
        },
    ],
    compile: (input) => [section('anvandning', 'Lokalens användning', freeTextParagraphs(input.premises_usage, 'premises_usage'))],
};
export const skick = {
    ...LEGAL,
    blockKey: 'lokalhyra.skick',
    version: 'v1',
    title: 'Lokalens skick',
    summary: 'The condition the premises are let in (optionally per annex) and an optional inspection protocol.',
    slots: [
        selectSlot('premises_condition', 'Lokalen hyrs ut i', PREMISES_CONDITION_OPTIONS, {
            required: true,
            help: 'Tilläggsavtal för hyresgästanpassning skapas som ett kopplat avtal (under ”Kopplade avtal” högst upp), inte som bilaga. Bilagan här specificerar enbart lokalens anpassade skick.',
        }),
        attachmentSlot('condition_annex', 'Bilaga för lokalens skick', {
            required: true,
            repeats: true,
            visibleWhen: whenOption('premises_condition', optionOf(PREMISES_CONDITION_OPTIONS, 'as_is_adapted_per_annex'), optionOf(PREMISES_CONDITION_OPTIONS, 'adapted_with_fitout_per_annex')),
            help: 'Välj eller skapa en eller flera bilagor som specificerar lokalens anpassade skick.',
        }),
        {
            key: 'inspection_protocol_included',
            label: 'Bifoga besiktnings- och åtgärdsprotokoll',
            type: 'boolean',
            help: 'Lägg till en beskrivning av lokalens skick vid avtalets ingående via ett besiktnings- och åtgärdsprotokoll som bilaga.',
        },
        attachmentSlot('inspection_protocol_annex', 'Bilaga för besiktnings- och åtgärdsprotokoll', {
            required: true,
            visibleWhen: { key: 'inspection_protocol_included', equals: true },
            help: 'Välj eller skapa besiktnings- och åtgärdsprotokollet.',
        }),
    ],
    compile: (input, ctx) => {
        const condition = optionValue(PREMISES_CONDITION_OPTIONS, input.premises_condition) ?? str(input.premises_condition);
        const perAnnex = condition?.match(/^(.*enligt) bilaga$/i);
        const conditionText = condition && perAnnex && hasAttachment(input.condition_annex)
            ? [val(perAnnex[1], { sourceKey: 'premises_condition' }), t(' '), ...annexOrRule(ctx, input.condition_annex, 'condition_annex')]
            : [val(condition, { blankWidth: 'long', sourceKey: 'premises_condition' })];
        return {
            attachments: contextAttachments(ctx),
            nodes: [
                section('skick', 'Lokalens skick', nodes(details([row('Lokalen hyrs ut i', conditionText)]), shown(ctx, !!input.inspection_protocol_included)
                    ? p([
                        t('En beskrivning av lokalens skick vid avtalets ingående samt uppgift om vem som på tillträdesdagen i förekommande fall ska ha ombesörjt respektive bekostat åtgärdandet av brister eller överenskomna ändringar av lokalen framgår av bifogat besiktnings- och åtgärdsprotokoll'),
                        ...(hasAttachment(input.inspection_protocol_annex)
                            ? [t(' ('), ...annexOrRule(ctx, input.inspection_protocol_annex, 'inspection_protocol_annex'), t(')')]
                            : []),
                        t('.'),
                    ])
                    : null)),
            ],
        };
    },
};
/* ------------------------------------------------------------------ */
/* §§ Hyra, årsvis höjning                                             */
/* ------------------------------------------------------------------ */
export const hyra = {
    ...LEGAL,
    blockKey: 'lokalhyra.hyra',
    version: 'v1',
    title: 'Hyra',
    summary: 'Yearly rent, invoicing frequency, payment method and late-payment interest.',
    slots: [
        { key: 'rent_amount', label: 'Årshyra (kr/år)', type: 'currency', required: true },
        selectSlot('payment_frequency', 'Hyran aviseras', PAYMENT_FREQUENCY_OPTIONS, { required: true, default: 'kvartalsvis' }),
        selectSlot('payment_type', 'Betalningssätt', PAYMENT_TYPE_OPTIONS, { required: true, default: 'per_invoice' }),
        {
            key: 'payment_account',
            label: 'Hyresvärdens konto (BG/PG + nummer)',
            type: 'text',
            help: 'T.ex. BG 123-4567',
            visibleWhen: whenOption('payment_type', optionOf(PAYMENT_TYPE_OPTIONS, 'plusgiro'), optionOf(PAYMENT_TYPE_OPTIONS, 'bankgiro')),
        },
    ],
    compile: (input, ctx) => {
        const frequency = optionValue(PAYMENT_FREQUENCY_OPTIONS, input.payment_frequency);
        const paymentType = optionValue(PAYMENT_TYPE_OPTIONS, input.payment_type);
        const period = frequency === 'månadsvis' ? 'kalendermånads' : frequency === 'årsvis' ? 'kalenderårs' : 'kalenderkvartals';
        return [
            section('hyra', 'Hyra', [
                p([t('Årshyran uppgår till '), numVal(ctx, input, 'rent_amount', { suffix: ' kr' }), t('.')]),
                p([
                    t('Hyran aviseras '),
                    strong([val(frequency, { blankWidth: 'short', sourceKey: 'payment_frequency' })]),
                    t(` och ska betalas utan anfordran i förskott senast sista vardagen före varje ${period} början.`),
                ]),
                singleChoice([
                    option(ticked(ctx, paymentType === 'Enligt avisering i faktura'), 'Hyran betalas enligt avisering.', { code: 'per_invoice' }),
                    option(ticked(ctx, paymentType === 'Plusgiro' || paymentType === 'Bankgiro'), [t('Hyran betalas genom insättning på Hyresvärdens konto: '), textVal(input, 'payment_account')], { code: 'account' }),
                ], 'payment_type'),
                p('Vid försenad hyresbetalning ska hyresgästen betala dröjsmålsränta och ersättning för betalningspåminnelse, inkasso och dylikt enligt lag.'),
            ]),
        ];
    },
};
const INDEX_STANDARD_BASE_YEAR = '2020';
export const arligHojning = {
    ...LEGAL,
    blockKey: 'lokalhyra.arlig-hojning',
    version: 'v1',
    title: 'Hyrans årsvisa höjning',
    summary: 'Annual rent adjustment: KPI indexation (standard or fully manual clause), fixed increase, or indexation per annex.',
    slots: [
        selectSlot('rent_increase_model', 'Hyreshöjningsmodell', RENT_INCREASE_OPTIONS, { required: true }),
        attachmentSlot('rent_index_annex', 'Bilaga – indexregleringsklausul', {
            required: true,
            visibleWhen: whenOption('rent_increase_model', optionOf(RENT_INCREASE_OPTIONS, 'index_per_annex')),
            help: 'Välj eller skapa en indexregleringsbilaga (mall: Indexavtal).',
        }),
        {
            key: 'rent_fixed_increase',
            label: 'Fast höjningsvillkor',
            type: 'textarea',
            help: 'Beskriv villkoren för hyreshöjning',
            visibleWhen: whenOption('rent_increase_model', optionOf(RENT_INCREASE_OPTIONS, 'fixed_increase')),
        },
        {
            key: 'rent_base_amount',
            label: 'Bashyra (kr eller %)',
            type: 'text',
            help: 'T.ex. 200 000 kr eller 100 %',
            visibleWhen: whenOption('rent_increase_model', optionOf(RENT_INCREASE_OPTIONS, 'kpi_index')),
        },
        {
            key: 'rent_index_base_year',
            label: 'Avvikande bastal (år, om annat än standard)',
            type: 'text',
            help: 'Lämna tomt för standardbastal (2020)',
            visibleWhen: whenOption('rent_increase_model', optionOf(RENT_INCREASE_OPTIONS, 'kpi_index')),
        },
        {
            key: 'rent_index_manual',
            label: 'Skriv indexregleringsklausulen helt manuellt',
            type: 'boolean',
            help: 'Markera för att ersätta standardtexten med en egen formulering av indexregleringsklausulen.',
            visibleWhen: whenOption('rent_increase_model', optionOf(RENT_INCREASE_OPTIONS, 'kpi_index')),
        },
        {
            key: 'rent_index_manual_text',
            label: 'Indexregleringsklausul (egen text)',
            type: 'textarea',
            required: true,
            help: 'Skriv hela indexregleringsklausulen som den ska visas i avtalet.',
            visibleWhen: { all: [whenOption('rent_increase_model', optionOf(RENT_INCREASE_OPTIONS, 'kpi_index')), { key: 'rent_index_manual', equals: true }] },
        },
    ],
    compile: (input, ctx) => {
        const model = optionValue(RENT_INCREASE_OPTIONS, input.rent_increase_model);
        const manual = !!input.rent_index_manual;
        const baseYear = str(input.rent_index_base_year);
        const indexBaseYear = baseYear ?? INDEX_STANDARD_BASE_YEAR;
        const modelLabel = model === RENT_FIXED ? 'Fast höjning hyrestid 1–3 år' : model;
        const baseRow = row('Bashyra (kr eller %)', textVal(input, 'rent_base_amount'));
        return {
            attachments: contextAttachments(ctx),
            nodes: [
                section('arlig-hojning', 'Hyrans årsvisa höjning', nodes(details([row('Modell', val(modelLabel, { blankWidth: 'long', sourceKey: 'rent_increase_model' }))]), ...(shown(ctx, model === RENT_FIXED)
                    ? [
                        details([row('Fast höjningsvillkor', textVal(input, 'rent_fixed_increase', 'long'))]),
                        p('Om hyrestiden enligt p. 4 är längre än 1 år men kortare än tre år, ska hyran enligt p. 7 höjas enligt ovan angivna villkor.'),
                    ]
                    : []), ...(shown(ctx, model === RENT_KPI && manual)
                    ? [details([baseRow]), ...freeTextParagraphs(input.rent_index_manual_text, 'rent_index_manual_text')]
                    : []), ...(shown(ctx, model === RENT_KPI && !manual)
                    ? [
                        details([
                            baseRow,
                            ...(shown(ctx, !!baseYear) ? [row('Avvikande bastal', textVal(input, 'rent_index_base_year', 'short'))] : []),
                        ]),
                        p([
                            t('Om hyrestiden enligt p. 4 är minst tre år, ska hyran enligt p. 7 indexeras årligen. Av det i kontraktet angivna hyresbeloppet ska '),
                            textVal(input, 'rent_base_amount'),
                            t(' utgöra bashyra. Under hyrestiden ska med hänsyn till förändringarna i konsumentprisindex (totalindex med '),
                            baseYear ? val(baseYear, { sourceKey: 'rent_index_base_year' }) : t(INDEX_STANDARD_BASE_YEAR),
                            t(' som basår) tillägg till hyresbeloppet utgå med en viss procent av bashyran enligt nedanstående grunder.'),
                        ]),
                        p([
                            t('För hyresavtal som börjar löpa någon gång under tiden 1/1–30/6 anses bashyran anpassad till indextalet för oktober månad året innan. För hyresavtal som börjar löpa någon gång under tiden 1/7–31/12 anses bashyran i stället anpassad till indextalet för oktober månad under samma tid. Indextalet för den oktober månad till vilken bashyran enligt ovan anses anpassad utgör bastal såvida inte annat avtalats'),
                            ...(baseYear ? [t(': indextalet för oktober månad år '), val(baseYear, { sourceKey: 'rent_index_base_year' })] : []),
                            t('.'),
                        ]),
                        p('Skulle indextalet någon påföljande oktober månad ha stigit i förhållande till bastalet, ska tillägg utgå med det procenttal varmed indextalet ändrats i förhållande till bastalet. I fortsättningen ska tillägg utgå i förhållande till indexändringarna, varvid hyresförändringen beräknas på basis av den procentuella förändringen mellan bastalet och indextalet för respektive oktober månad. Tillägget kan bli såväl högre som lägre mellan olika år. Utgående hyra ska dock aldrig sättas lägre än det i kontraktet angivna hyresbeloppet. Hyresändringen sker alltid fr.o.m. den 1 januari efter det att oktoberindex föranlett omräkning.'),
                        // The 335,92 divisor is specific to basår 2020: the paragraph only holds for the standard basår.
                        shown(ctx, !baseYear)
                            ? p(`Om bastalet utgörs av indextal för månad med fastställt indextal med basår 1980 (oktober månad t.o.m. år 2025) ska indextalet omräknas till index med basår ${indexBaseYear} genom att bastalet divideras med medeltalet av KPI för ${indexBaseYear} (335,92 med basår 1980) och kvoten multipliceras med 100, avrundat till två decimaler.`)
                            : null,
                    ]
                    : []), shown(ctx, model === RENT_ANNEX)
                    ? p([
                        t('Indexreglering av hyran sker enligt särskild bilaga. '),
                        ...annexOrBlank(ctx, input.rent_index_annex, 'rent_index_annex'),
                        t('.'),
                    ])
                    : null)),
            ],
        };
    },
};
/* ------------------------------------------------------------------ */
/* § Taxebundna tillägg (Taxebundna tillägg.docx)                       */
/* ------------------------------------------------------------------ */
const elVisible = (mode) => ({ key: 'el_mode', equals: mode });
const elFastVisible = (modell) => ({
    all: [elVisible('fast_uppraknings'), { key: 'el_fast_uppraknings_modell', equals: modell }],
});
const driftPriced = (post) => ({
    any: [
        { key: 'drift_meter_posts', includes: post },
        { key: 'drift_andel_posts', includes: post },
    ],
});
/**
 * In the rendered contract only the chosen alternative prints, without a
 * checkbox (UX feedback 2026-05-28); a blank form prints every alternative.
 */
const taxeOption = (ctx, active, content) => ctx.mode !== 'blank' && !active ? null : group(content);
function driftTable(ctx, input, postKeys) {
    const posts = DRIFT_TABLE_POSTS.filter((post) => postKeys.includes(post.key)).map((post) => {
        const amount = input[`drift_amount_${post.key}`];
        return { ...post, amount: typeof amount === 'number' && Number.isFinite(amount) ? amount : 0 };
    });
    const visible = ctx.mode === 'blank' ? posts : posts.filter((post) => post.amount > 0);
    if (visible.length === 0 && ctx.mode !== 'blank')
        return p('(Inga driftposter angivna.)', { variant: 'fine_print' });
    const total = posts.reduce((sum, post) => sum + post.amount, 0);
    const amountCell = (amount, sourceKey) => [
        p(amount > 0 ? [amountVal(ctx, amount, sourceKey, ' kr/år')] : [val(null, { blankWidth: 'short', sourceKey }), t(' kr/år')], { align: 'right' }),
    ];
    const body = visible.map((post) => ({
        cells: [{ content: [p(post.label)] }, { content: amountCell(post.amount, `drift_amount_${post.key}`) }],
    }));
    return table([
        { key: 'post', header: [t('Driftkostnad')] },
        { key: 'amount', header: [t('Preliminär debitering vid avtalets ingående (kr/år)')], align: 'right' },
    ], [
        ...body,
        { variant: 'total', cells: [{ content: [p([strong('Totalt')])] }, { content: amountCell(total, 'drift_total') }] },
    ], { variant: 'data' });
}
/**
 * Each media type lands in exactly one category, by precedence (eget avtal >
 * mätaravläsning > andelstal > endast bilaga) — a render-time guard for when
 * the editor failed to keep the four multi-selects exclusive.
 */
function driftAssignments(input) {
    const assigned = new Set();
    const take = (raw) => arr(raw).filter((key) => {
        if (assigned.has(key))
            return false;
        assigned.add(key);
        return true;
    });
    const eget = take(input.drift_eget_posts);
    const meter = take(input.drift_meter_posts);
    const andel = take(input.drift_andel_posts);
    const bilaga = take(input.drift_bilaga_posts);
    return { eget, meter, andel, bilaga };
}
const driftLabels = (keys, sourceKey) => val(keys.length > 0 ? keys.map(driftMediaLabel).join(', ') : null, { blankWidth: 'medium', sourceKey });
export const forbrukning = {
    ...LEGAL,
    blockKey: 'lokalhyra.forbrukning',
    version: 'v1',
    title: 'Taxebundna tillägg: El, värme, kyla och drift',
    summary: 'Tariff-based supplements: electricity (own contract, metered, share-based or fixed with escalation) and per-media-type heating/cooling/operating costs.',
    slots: [
        selectSlot('el_mode', 'Modell – el', EL_MODE_OPTIONS, { required: true, default: 'mataravlasning' }),
        { key: 'el_meter_preliminary_yearly', label: 'Preliminär ersättning vid avtalets ingående (kr/år)', type: 'currency', visibleWhen: elVisible('mataravlasning') },
        {
            key: 'el_meter_andel_percent',
            label: 'Lokalens andel av Fastighetens totala uthyrbara yta (%)',
            type: 'text',
            visibleWhen: elVisible('mataravlasning'),
            help: 'Används om förbrukning inte kan mätas separat med undermätare.',
        },
        { key: 'el_andelstal_percent', label: 'Lokalens andel av Fastighetens totala uthyrbara yta (%)', type: 'text', visibleWhen: elVisible('andelstal') },
        { key: 'el_andelstal_yearly', label: 'Motsvarande preliminärt hyrestillägg vid avtalets ingående (kr/år)', type: 'currency', visibleWhen: elVisible('andelstal') },
        { key: 'el_fast_yearly', label: 'Fast hyrestillägg vid avtalets ingående (kr/år)', type: 'currency', visibleWhen: elVisible('fast_uppraknings') },
        selectSlot('el_fast_uppraknings_modell', 'Modell för årlig uppräkning', UPPRAKNINGS_MODELL_OPTIONS, {
            default: 'fast_procent',
            visibleWhen: elVisible('fast_uppraknings'),
        }),
        { key: 'el_fast_procent_per_year', label: 'Procentsats per år (%)', type: 'text', visibleWhen: elFastVisible('fast_procent') },
        { key: 'el_fast_kpi_base_month_year', label: 'Bastal (månad år, t.ex. oktober 2024)', type: 'text', visibleWhen: elFastVisible('kpi') },
        {
            key: 'el_fast_kpi_comparison_month',
            label: 'Jämförelsetal — månad närmast före uppräkningstidpunkten',
            type: 'text',
            help: 't.ex. oktober',
            visibleWhen: elFastVisible('kpi'),
        },
        {
            key: 'el_fast_faktisk_notice_date',
            label: 'Senaste skriftliga aviseringsdatum varje år',
            type: 'text',
            help: 't.ex. 30 april',
            visibleWhen: elFastVisible('faktisk_kostnad'),
        },
        {
            key: 'drift_eget_posts',
            label: 'Eget avtal — driftposter hyresgästen tecknar eget avtal för',
            type: 'multi_select',
            options: toSlotOptions(DRIFT_MEDIA_OPTIONS),
            help: 'Hyresgästen tecknar eget avtal direkt för dessa driftposter.',
        },
        {
            key: 'drift_meter_posts',
            label: 'Mätaravläsning — driftposter som avräknas mot uppmätt förbrukning',
            type: 'multi_select',
            options: toSlotOptions(DRIFT_MEDIA_OPTIONS),
            help: 'Preliminärt hyrestillägg som avräknas mot uppmätt förbrukning/andel.',
        },
        {
            key: 'drift_andel_posts',
            label: 'Andelstal — driftposter utifrån lokalens andel av Fastigheten',
            type: 'multi_select',
            options: toSlotOptions(DRIFT_MEDIA_OPTIONS),
            help: 'Preliminärt hyrestillägg utifrån lokalens andel av Fastighetens totala yta.',
        },
        {
            key: 'drift_bilaga_posts',
            label: 'Endast bilaga — driftposter som regleras i driftskostnadsklausul-bilaga',
            type: 'multi_select',
            options: toSlotOptions(DRIFT_MEDIA_OPTIONS),
            help: 'Dessa driftposter definieras enbart i den bifogade driftskostnadsklausulen (se nedan).',
        },
        ...DRIFT_TABLE_POSTS.map((post, i) => ({
            key: `drift_amount_${post.key}`,
            label: `${post.label} (kr/år)`,
            type: 'currency',
            visibleWhen: driftPriced(post.key),
            ...(i === 0 ? { help: 'Anges för de driftposter du placerat under mätaravläsning eller andelstal.' } : {}),
        })),
        {
            key: 'drift_andel_percent',
            label: 'Lokalens andel av Fastighetens totala uthyrbara yta (%)',
            type: 'text',
            help: 'Anges vid mätaravläsning eller andelstal — t.ex. 8.',
        },
        attachmentSlot('drift_annex', 'Bilaga – driftskostnadsklausul', {
            help: 'Reglerar övriga driftkostnader. Obligatorisk om någon driftpost valts som "Endast bilaga".',
        }),
    ],
    compile: (input, ctx) => {
        const el = optionValue(EL_MODE_OPTIONS, input.el_mode);
        const modell = optionValue(UPPRAKNINGS_MODELL_OPTIONS, input.el_fast_uppraknings_modell);
        const fast = el === 'fast_uppraknings';
        const drift = driftAssignments(input);
        const slot = (key) => inlineSlot(ctx, input[key], key);
        const elSection = sub('forbrukning.el', 'Ersättning för el', nodes(taxeOption(ctx, el === 'eget_avtal', [
            leadP('Eget avtal.', [' Hyresgästen tecknar eget elavtal direkt med elleverantör och nätägare. Hyresvärden ansvarar inte för elleverans till Lokalen.']),
        ]), taxeOption(ctx, el === 'mataravlasning', [
            leadP('Mätaravläsning.', [
                ' Hyresgästen ska genom ett årligt preliminärt hyrestillägg betala för sin uppmätta förbrukning av el (elleverans och nätabonnemang) som tillhandahålls av Hyresvärden. Preliminär ersättning utgår med ',
                slot('el_meter_preliminary_yearly'),
                ' kronor per år.',
            ]),
            p('Den preliminära ersättningen avräknas kalenderårsvis i efterskott, senast den 30 juni för föregående kalenderår. Upphör hyresavtalet vid annan tidpunkt än årsskifte sker avräkning mot faktisk förbrukning i samband med avtalets upphörande, eller så snart avläsning av mätare kan ske.'),
            p('För det fall Hyresgästens enskilda förbrukning inte kan mätas med undermätare ska Hyresgästens andel av de totala kostnaderna motsvara ytan av den förhyrda Lokalen i förhållande till Fastighetens totala uthyrbara yta.'),
            p([t('Vid avtalets undertecknande uppgår Hyresgästens andel till '), slot('el_meter_andel_percent'), t(' %.')]),
            p('Hyresgästen har inte rätt till ränta på preliminärdebiterat belopp.'),
        ]), taxeOption(ctx, el === 'andelstal', [
            leadP('Andelstal.', [
                ' Hyresgästen ska genom ett årligt preliminärt hyrestillägg betala för sin andel av Fastighetens totala kostnad för el (elleverans och nätabonnemang) som tillhandahålls av Hyresvärden.',
            ]),
            p('Hyresgästens andel av de totala kostnaderna motsvarar ytan av den förhyrda Lokalen i förhållande till Fastighetens totala uthyrbara yta.'),
            p([
                t('Vid avtalets undertecknande uppgår Hyresgästens andel till '),
                slot('el_andelstal_percent'),
                t(' %, motsvarande ett hyrestillägg om '),
                slot('el_andelstal_yearly'),
                t(' kronor per år.'),
            ]),
            p('Det preliminära hyrestillägget avräknas kalenderårsvis i efterskott, senast den 30 juni för föregående kalenderår.'),
            p('Hyresgästen har inte rätt till ränta på preliminärdebiterat belopp.'),
        ]), taxeOption(ctx, fast, [
            leadP('Fast tillägg med årlig uppräkning.', [
                ' Hyresgästen ska genom ett fast hyrestillägg betala ersättning för el. Hyrestillägget uppgår vid avtalets ingående till ',
                slot('el_fast_yearly'),
                ' kronor per år.',
            ]),
            p('Hyrestillägget ska årligen räknas upp enligt följande:'),
            group(nodes(taxeOption(ctx, fast && modell === 'fast_procent', [
                leadP('Fast procentsats', [' — med en procentsats om ', slot('el_fast_procent_per_year'), ' % per år.']),
            ]), taxeOption(ctx, fast && modell === 'kpi', [
                leadP('KPI', [
                    ' — i proportion till förändringen i konsumentprisindex (KPI), med ',
                    slot('el_fast_kpi_base_month_year'),
                    ' som bastal och ',
                    slot('el_fast_kpi_comparison_month'),
                    ' månad närmast före uppräkningstidpunkten som jämförelsetal. Uppräkning sker per den 1 januari. Hyrestillägget ska aldrig understiga föregående års nivå.',
                ]),
            ]), taxeOption(ctx, fast && modell === 'faktisk_kostnad', [
                leadP('Faktisk kostnad', [
                    ' — i enlighet med Hyresvärdens faktiska kostnadsutveckling för de driftposter som hyrestillägget avser. Hyresvärden ska senast den ',
                    slot('el_fast_faktisk_notice_date'),
                    ' varje år skriftligen meddela Hyresgästen det justerade hyrestillägget, med uppgift om underliggande kostnadsförändringar. Justering sker per den 1 januari med fakturering retroaktivt för perioden januari till och med aviseringsdatumet.',
                ]),
            ])), { variant: 'indent' }),
        ])));
        const driftSection = sub('forbrukning.drift', 'Ersättning för värme, kyla, drift och skötsel m.m.', nodes(p('Täcker värme, VA, kyla, snöröjning, ventilation, fastighetsel och fastighetsskötsel. Respektive driftpost regleras enligt det alternativ som anges nedan.'), shown(ctx, drift.eget.length > 0)
            ? leadP('Eget avtal.', [' Hyresgästen tecknar eget avtal direkt för följande driftposter: ', driftLabels(drift.eget, 'drift_eget_posts'), '.'])
            : null, shown(ctx, drift.meter.length > 0)
            ? group([
                leadP('Mätaravläsning.', [
                    ' Hyresgästen ska genom ett årligt preliminärt hyrestillägg betala för sin uppmätta andel av Fastighetens totala kostnad för nedanstående driftposter som tillhandahålls av Hyresvärden.',
                ]),
                p('Kostnader som omfattas av hyrestillägget specificeras i tabellen nedan:'),
                driftTable(ctx, input, drift.meter),
                p('Den preliminära ersättningen avräknas kalenderårsvis i efterskott, senast den 30 juni för föregående kalenderår. Upphör hyresavtalet vid annan tidpunkt än årsskifte sker avräkning mot faktisk förbrukning i samband med avtalets upphörande, eller så snart avläsning av mätare kan ske.'),
                p('För det fall Hyresgästens enskilda förbrukning inte kan mätas med undermätare ska Hyresgästens andel av de totala kostnaderna motsvara ytan av den förhyrda Lokalen i förhållande till Fastighetens totala uthyrbara yta.'),
                p([t('Vid avtalets undertecknande uppgår Hyresgästens andel till '), slot('drift_andel_percent'), t(' %.')]),
                p('Hyresgästen har inte rätt till ränta på preliminärdebiterat belopp.'),
            ])
            : null, shown(ctx, drift.andel.length > 0)
            ? group([
                leadP('Andelstal.', [
                    ' Hyresgästen ska genom ett årligt preliminärt hyrestillägg betala för sin andel av Fastighetens totala kostnad för nedanstående driftposter som tillhandahålls av Hyresvärden.',
                ]),
                p('Hyresgästens andel av de totala kostnaderna motsvarar ytan av den förhyrda Lokalen i förhållande till Fastighetens totala uthyrbara yta.'),
                p('Kostnader som omfattas av hyrestillägget specificeras i tabellen nedan:'),
                driftTable(ctx, input, drift.andel),
                p([t('Vid avtalets undertecknande uppgår Hyresgästens andel till '), slot('drift_andel_percent'), t(' %.')]),
                p('Det preliminära hyrestillägget avräknas kalenderårsvis i efterskott, senast den 30 juni för föregående kalenderår.'),
                p('Hyresgästen har inte rätt till ränta på preliminärdebiterat belopp.'),
            ])
            : null, shown(ctx, drift.bilaga.length > 0)
            ? leadP('Reglering enligt driftskostnadsklausul (bilaga).', [
                ' Följande driftposter regleras i den bifogade driftskostnadsklausulen: ',
                driftLabels(drift.bilaga, 'drift_bilaga_posts'),
                '. ',
                ...annexOrBlank(ctx, input.drift_annex, 'drift_annex'),
                '.',
            ])
            : null, shown(ctx, hasAttachment(input.drift_annex))
            ? leadP('Övrig driftkostnad.', [
                ' Övriga driftkostnader regleras i en separat driftskostnadsklausul som gäller som bilaga till detta avtal. ',
                ...annexOrBlank(ctx, input.drift_annex, 'drift_annex'),
                '.',
            ])
            : null, leadP('Justering av preliminärt belopp.', [
            ' Hyresvärden äger rätt att under löpande kalenderår justera det preliminära hyrestillägget om Hyresvärdens faktiska kostnader väsentligt avviker från det preliminära beloppet. Hyresvärden ska skriftligen meddela Hyresgästen om sådan justering senast 30 dagar innan den träder i kraft.',
        ]), leadP('Moms.', [' På hyrestillägg enligt denna paragraf tillkommer mervärdesskatt i den mån Hyresvärden är skattskyldig för uthyrningen.']), leadP('Betalning.', [
            ' Hyrestillägg enligt denna paragraf erläggs i förskott senast sista vardagen före varje kalendermånads eller kalenderkvartals början, i enlighet med vad som anges i ',
            ctx.refs.section('hyra'),
            ' (Hyrans betalning).',
        ])));
        const missingAnnex = ctx.mode !== 'blank' && drift.bilaga.length > 0 && !hasAttachment(input.drift_annex)
            ? [
                {
                    severity: 'warning',
                    code: 'drift_annex_required',
                    message: 'Bilaga – driftskostnadsklausul krävs när någon driftpost regleras enbart i bilaga.',
                    slotKey: 'drift_annex',
                },
            ]
            : [];
        return {
            attachments: contextAttachments(ctx),
            diagnostics: missingAnnex,
            nodes: [section('forbrukning', 'Taxebundna tillägg: El, värme, kyla och drift', [elSection, driftSection])],
        };
    },
};
/* ------------------------------------------------------------------ */
/* §§ Avfallshantering, bevakning                                      */
/* ------------------------------------------------------------------ */
const shareAndAmount = (ctx, input, shareKey, amountKey) => [
    t('Lokalens andel ska anses vara '),
    val(str(input[shareKey]), { blankWidth: 'short', sourceKey: shareKey }),
    t(' %. Hyrestillägget uppgår vid avtalets tecknande till '),
    ...(formatSlotNumber(ctx, input[amountKey]) != null
        ? [numVal(ctx, input, amountKey, { suffix: ' kr/år' })]
        : [val(null, { blankWidth: 'short', sourceKey: amountKey }), t(' kr/år')]),
];
export const avfallshantering = {
    ...LEGAL,
    blockKey: 'lokalhyra.avfallshantering',
    version: 'v1',
    title: 'Avfallshantering',
    summary: 'Waste handling: compactor use and who arranges and pays for waste removal.',
    slots: [
        {
            key: 'waste_compactor_allowed',
            label: 'Hyresgästen får nyttja Fastighetens komprimatorer',
            type: 'boolean',
            help: 'Valfritt — markera om Fastigheten har komprimatorer som hyresgästen får använda.',
        },
        selectSlot('waste_transport_mode', 'Borttransport av avfall', WASTE_TRANSPORT_OPTIONS, { required: true, default: 'tenant_own' }),
        { key: 'waste_share_percent', label: 'Lokalens andel av avfallskostnaden (%)', type: 'text', help: 't.ex. 12', visibleWhen: { key: 'waste_transport_mode', equals: 'landlord_coordinated' } },
        { key: 'waste_yearly_amount', label: 'Hyrestillägg för avfall vid avtalets tecknande (kr/år)', type: 'currency', visibleWhen: { key: 'waste_transport_mode', equals: 'landlord_coordinated' } },
        attachmentSlot('waste_annex', 'Bilaga – avfallshantering', {
            required: true,
            visibleWhen: whenOption('waste_transport_mode', optionOf(WASTE_TRANSPORT_OPTIONS, 'landlord_coordinated'), optionOf(WASTE_TRANSPORT_OPTIONS, 'per_annex')),
            help: 'Bilagan beskriver fördelningen / den särskilda regleringen.',
        }),
    ],
    compile: (input, ctx) => {
        const mode = optionValue(WASTE_TRANSPORT_OPTIONS, input.waste_transport_mode);
        const is = (m) => ticked(ctx, mode === m);
        return {
            attachments: contextAttachments(ctx),
            nodes: [
                section('avfallshantering', 'Avfallshantering', [
                    p('Hyresgästen ska tillse att all avfallshantering sker i enlighet med för Fastigheten vid var tid gällande bestämmelser och rekommendationer.'),
                    optionList([option(ticked(ctx, !!input.waste_compactor_allowed), 'Hyresgästen får nyttja Fastighetens komprimatorer.', { code: 'compactor' })], {
                        select: 'multi',
                        sourceKey: 'waste_compactor_allowed',
                    }),
                    p([strong('Borttransport av avfall')], { keepWithNext: true }),
                    singleChoice([
                        option(is('tenant_own'), 'Hyresgästen svarar för och bekostar borttransport av avfall som härrör från den verksamhet som Hyresgästen bedriver i Lokalen. Hyresgästen förbinder sig att med renhållningsentreprenör teckna och vidmakthålla kontrakt om borttransport av avfall.', { code: 'tenant_own' }),
                        option(is('landlord_coordinated'), [
                            t('Hyresvärden samordnar borttransport av avfall för flera hyresgästers verksamhet i Fastigheten. Hyresgästen ska som tillägg till hyran betala ersättning för Lokalens andel av kostnaden. '),
                            ...shareAndAmount(ctx, input, 'waste_share_percent', 'waste_yearly_amount'),
                            t('. '),
                            ...annexOrBlank(ctx, input.waste_annex, 'waste_annex'),
                            t('.'),
                        ], { code: 'landlord_coordinated' }),
                        option(is('included_in_rent'), 'Kostnaden för borttransport av avfall ingår i hyran.', { code: 'included_in_rent' }),
                        option(is('per_annex'), [t('Särskild reglering enligt bilaga. '), ...annexOrBlank(ctx, input.waste_annex, 'waste_annex'), t('.')], { code: 'per_annex' }),
                    ], 'waste_transport_mode'),
                ]),
            ],
        };
    },
};
export const bevakning = {
    ...LEGAL,
    blockKey: 'lokalhyra.bevakning',
    version: 'v1',
    title: 'Bevakning',
    summary: 'Whether the landlord provides security for the property, and the tenant’s share of its cost.',
    slots: [
        selectSlot('bevakning_mode', 'Bevakning av Fastigheten', BEVAKNING_OPTIONS, { required: true, default: 'tenant_own' }),
        { key: 'bevakning_share_percent', label: 'Lokalens andel av bevakningskostnaden (%)', type: 'text', help: 't.ex. 12', visibleWhen: { key: 'bevakning_mode', equals: 'landlord_provides' } },
        { key: 'bevakning_yearly_amount', label: 'Hyrestillägg för bevakning vid avtalets tecknande (kr/år)', type: 'currency', visibleWhen: { key: 'bevakning_mode', equals: 'landlord_provides' } },
    ],
    compile: (input, ctx) => {
        const mode = optionValue(BEVAKNING_OPTIONS, input.bevakning_mode);
        return [
            section('bevakning', 'Bevakning', [
                singleChoice([
                    option(ticked(ctx, mode === 'tenant_own'), 'Hyresvärden ombesörjer inte bevakning av Fastigheten. Har Hyresgästen behov av bevakning av sin verksamhet ska Hyresgästen själv ansvara för och bekosta denna.', { code: 'tenant_own' }),
                    option(ticked(ctx, mode === 'landlord_provides'), [
                        t('Hyresvärden tillhandahåller bevakning av Fastigheten. Hyresgästen ska som tillägg till hyran betala ersättning för Lokalens andel av kostnaden. '),
                        ...shareAndAmount(ctx, input, 'bevakning_share_percent', 'bevakning_yearly_amount'),
                        t('.'),
                    ], { code: 'landlord_provides' }),
                ], 'bevakning_mode'),
            ]),
        ];
    },
};
/* ------------------------------------------------------------------ */
/* §§ Säkerhet, besittningsskydd                                       */
/* ------------------------------------------------------------------ */
const PROPRIEBORGEN = whenOption('security_type', optionOf(SECURITY_TYPE_OPTIONS, 'proprieborgen'));
export const sakerhet = {
    ...LEGAL,
    blockKey: 'lokalhyra.sakerhet',
    version: 'v1',
    title: 'Säkerhet',
    summary: 'Security for the lease: none, deposit, bank guarantee, rent-guarantee insurance or a surety (proprieborgen) with the guarantor’s details.',
    slots: [
        selectSlot('security_type', 'Säkerhetstyp', SECURITY_TYPE_OPTIONS, { required: true }),
        { key: 'security_months', label: 'Antal månadshyror', type: 'number', help: 'T.ex. 3', visibleWhen: whenOption('security_type', optionOf(SECURITY_TYPE_OPTIONS, 'deposition')) },
        { key: 'security_amount', label: 'Belopp (kr)', type: 'currency', visibleWhen: whenOption('security_type', optionOf(SECURITY_TYPE_OPTIONS, 'deposition')) },
        { key: 'security_account', label: 'Depositionskonto (BG/PG + nummer)', type: 'text', help: 'Fyll i vid deposition', visibleWhen: whenOption('security_type', optionOf(SECURITY_TYPE_OPTIONS, 'deposition')) },
        {
            key: 'insurance_premium',
            label: 'Försäkringspremie (kr/år)',
            type: 'currency',
            help: 'Fyll i vid hyresgarantiförsäkring',
            visibleWhen: whenOption('security_type', optionOf(SECURITY_TYPE_OPTIONS, 'hyresgaranti_individuell'), optionOf(SECURITY_TYPE_OPTIONS, 'hyresgaranti_grupp')),
        },
        withAutofill({ key: 'guarantor_name', label: 'Borgensman – namn', type: 'text', visibleWhen: PROPRIEBORGEN }, { kind: 'party', role: 'guarantor', ordinal: 0, field: 'name' }),
        withAutofill({ key: 'guarantor_org_number', label: 'Borgensman – personnr/org.nr', type: 'text', visibleWhen: PROPRIEBORGEN }, { kind: 'party', role: 'guarantor', ordinal: 0, field: 'legalId' }),
        withAutofill({ key: 'guarantor_address', label: 'Borgensman – adress', type: 'textarea', visibleWhen: PROPRIEBORGEN }, { kind: 'party', role: 'guarantor', ordinal: 0, field: 'address' }),
        withAutofill({ key: 'guarantor_phone', label: 'Borgensman – telefon', type: 'text', visibleWhen: PROPRIEBORGEN }, { kind: 'party', role: 'guarantor', ordinal: 0, field: 'phone' }),
        withAutofill({ key: 'guarantor_email', label: 'Borgensman – e-post', type: 'text', visibleWhen: PROPRIEBORGEN }, { kind: 'party', role: 'guarantor', ordinal: 0, field: 'email' }),
        { key: 'guarantor_end_date', label: 'Borgensåtagande – slutdatum', type: 'date', visibleWhen: PROPRIEBORGEN },
    ],
    compile: (input, ctx) => {
        const type = optionValue(SECURITY_TYPE_OPTIONS, input.security_type);
        return [
            section('sakerhet', 'Säkerhet', nodes(details([
                row('Säkerhetstyp', val(type, { blankWidth: 'long', sourceKey: 'security_type' })),
                ...(shown(ctx, type === 'Deposition')
                    ? [
                        row('Antal månadshyror', numVal(ctx, input, 'security_months')),
                        row('Belopp (kr)', numVal(ctx, input, 'security_amount')),
                        row('Depositionskonto', textVal(input, 'security_account')),
                    ]
                    : []),
                ...(shown(ctx, type === SECURITY_TYPE_OPTIONS[3].value || type === SECURITY_TYPE_OPTIONS[4].value)
                    ? [row('Försäkringspremie (kr/år)', numVal(ctx, input, 'insurance_premium'))]
                    : []),
            ]), shown(ctx, type === 'Proprieborgen')
                ? group([
                    details([
                        row('Namn', textVal(input, 'guarantor_name')),
                        row('Personnr/org.nr', textVal(input, 'guarantor_org_number')),
                        row('Adress', textVal(input, 'guarantor_address', 'long')),
                        row('Telefon', textVal(input, 'guarantor_phone')),
                        row('E-post', textVal(input, 'guarantor_email')),
                        row('Borgensåtagande – slutdatum', val(str(input.guarantor_end_date), { format: 'date', sourceKey: 'guarantor_end_date' })),
                    ]),
                ], { title: 'Borgensman' })
                : null)),
        ];
    },
};
export const besittningsskydd = {
    ...LEGAL,
    blockKey: 'lokalhyra.besittningsskydd',
    version: 'v1',
    title: 'Besittningsskydd',
    summary: 'Whether the tenant keeps security of tenure, or waives it (approved by the rent tribunal or not subject to review) for a stated period.',
    slots: [
        selectSlot('possession_protection', 'Besittningsskydd', POSSESSION_OPTIONS, { required: true, default: 'has_protection' }),
        { key: 'possession_waiver_from', label: 'Avståendets giltighetstid fr.o.m.', type: 'date', visibleWhen: whenOption('possession_protection', optionOf(POSSESSION_OPTIONS, 'waived_approved'), optionOf(POSSESSION_OPTIONS, 'waived_no_review')) },
        { key: 'possession_waiver_to', label: 'Avståendets giltighetstid t.o.m.', type: 'date', visibleWhen: whenOption('possession_protection', optionOf(POSSESSION_OPTIONS, 'waived_approved'), optionOf(POSSESSION_OPTIONS, 'waived_no_review')) },
    ],
    compile: (input, ctx) => {
        const protection = optionValue(POSSESSION_OPTIONS, input.possession_protection);
        const waived = protection != null && POSSESSION_WAIVERS.includes(protection);
        return [
            section('besittningsskydd', 'Besittningsskydd', nodes(details([
                row('Besittningsskydd', val(protection, { blankWidth: 'long', sourceKey: 'possession_protection' })),
                ...(shown(ctx, waived)
                    ? [
                        row('Avstående fr.o.m.', val(str(input.possession_waiver_from), { format: 'date', sourceKey: 'possession_waiver_from' })),
                        row('Avstående t.o.m.', val(str(input.possession_waiver_to), { format: 'date', sourceKey: 'possession_waiver_to' })),
                    ]
                    : []),
            ]), shown(ctx, waived) ? p('Enligt särskilt upprättad överenskommelse om avstående från besittningsskydd.') : null)),
        ];
    },
};
/* ------------------------------------------------------------------ */
/* §§ Lås, underhåll, ombyggnad                                        */
/* ------------------------------------------------------------------ */
export const lasStold = {
    ...LEGAL,
    blockKey: 'lokalhyra.las-stold',
    version: 'v1',
    title: 'Lokalens lås- och stöldskyddsanordningar',
    summary: 'Who equips the premises with locks and theft protection — tenant, landlord, or per annex.',
    slots: [
        selectSlot('lock_responsibility', 'Lås- och stöldskyddsanordningar', LOCK_OPTIONS, { required: true, default: 'tenant' }),
        attachmentSlot('lock_annex', 'Bilaga för lås- och stöldskydd', {
            required: true,
            visibleWhen: whenOption('lock_responsibility', optionOf(LOCK_OPTIONS, 'per_annex')),
            help: 'Välj eller skapa bilagan med särskilda villkor om lås och stöldskydd.',
        }),
    ],
    compile: (input, ctx) => {
        const lock = optionValue(LOCK_OPTIONS, input.lock_responsibility);
        const body = lock === 'Enligt särskilda villkor i bilaga'
            ? [
                t('Ansvar för anordnandet av lokalens lås- och stöldskyddsanordningar ska följa av särskilda villkor enligt '),
                ...annexOrRule(ctx, input.lock_annex, 'lock_annex'),
                t('.'),
            ]
            : lock === 'Hyresvärden ansvarar'
                ? [
                    t('Hyresvärden ansvarar för att lokalen utrustas med sådana lås- och stöldskyddsanordningar som krävs för hyresgästens affärs- och företagsverksamhet och godkänns av hyresgästens företagsförsäkring.'),
                ]
                : [
                    t('Hyresgästen ansvarar för att lokalen utrustas med sådana lås- och stöldskyddsanordningar som krävs för hyresgästens affärs- och företagsverksamhet och godkänns av hyresgästens företagsförsäkring.'),
                ];
        return { attachments: contextAttachments(ctx), nodes: [section('las-stold', 'Lokalens lås- och stöldskyddsanordningar', [p(body)])] };
    },
};
/**
 * Every alternative printed with a ☑/☐ box; the chosen annex alternative
 * carries its "(Bilaga N)" suffix after the body, or after the heading when
 * the alternative has no body.
 */
function clauseChoice(ctx, clauses, selected, annexValue, annex, sourceKey) {
    return singleChoice(clauses.map((clause) => {
        const checked = ticked(ctx, selected === clause.value);
        const suffix = checked && clause.value === annexValue && annex ? [t(' ('), ...annex, t(')')] : [];
        return {
            ...option(checked, [strong([t(clause.heading), ...(clause.body ? [] : suffix)])], { code: clause.code }),
            ...(clause.body ? { children: [p([t(clause.body), ...suffix])] } : {}),
        };
    }), sourceKey);
}
const annexIfSet = (ctx, value, sourceKey) => hasAttachment(value) ? annexOrRule(ctx, value, sourceKey) : null;
export const underhall = {
    ...LEGAL,
    blockKey: 'lokalhyra.underhall',
    version: 'v1',
    title: 'Underhållsansvar',
    summary: 'Maintenance responsibility for the premises, common areas and (optionally) ancillary spaces, and the remedy if the tenant neglects it.',
    slots: [
        selectSlot('maintenance_premises', 'Underhåll av lokalen', MAINTENANCE_PREMISES_SELECT, { required: true, default: 'landlord' }),
        attachmentSlot('maintenance_premises_annex', 'Bilaga – gränsdragningslista (lokalen)', {
            required: true,
            visibleWhen: whenOption('maintenance_premises', optionOf(MAINTENANCE_PREMISES_SELECT, 'per_annex')),
            help: 'Välj eller skapa gränsdragningslistan för underhållsansvaret.',
        }),
        selectSlot('maintenance_common', 'Allmänna och gemensamma utrymmen', MAINTENANCE_COMMON_SELECT, { required: true, default: 'landlord' }),
        attachmentSlot('maintenance_common_annex', 'Bilaga – allmänna/gemensamma utrymmen', {
            required: true,
            visibleWhen: whenOption('maintenance_common', optionOf(MAINTENANCE_COMMON_SELECT, 'per_annex')),
            help: 'Välj eller skapa bilagan med den avvikande överenskommelsen.',
        }),
        {
            key: 'ancillary_spaces_maintenance',
            label: 'Reglera underhåll av tillhörande biutrymmen',
            type: 'boolean',
            help: 'Aktiverar tilläggsparagraf 13.3. Markera om hyresobjektet omfattar tillhörande biutrymmen, t.ex. förråd.',
        },
        selectSlot('maintenance_ancillary', 'Underhåll av biutrymmen', MAINTENANCE_ANCILLARY_SELECT, {
            required: true,
            default: 'landlord',
            visibleWhen: { key: 'ancillary_spaces_maintenance', equals: true },
        }),
        attachmentSlot('maintenance_ancillary_annex', 'Bilaga – gränsdragningslista (biutrymmen)', {
            required: true,
            visibleWhen: {
                all: [
                    { key: 'ancillary_spaces_maintenance', equals: true },
                    whenOption('maintenance_ancillary', optionOf(MAINTENANCE_ANCILLARY_SELECT, 'per_annex')),
                ],
            },
            help: 'Välj eller skapa gränsdragningslistan för biutrymmenas underhåll.',
        }),
    ],
    compile: (input, ctx) => {
        const perAnnex = 'Annan fördelning enligt bilaga (gränsdragningslista)';
        return {
            attachments: contextAttachments(ctx),
            nodes: [
                section('underhall', 'Underhållsansvar', nodes(sub('underhall.lokalen', 'Avseende lokalen', [
                    clauseChoice(ctx, MAINTENANCE_PREMISES_CLAUSES, optionValue(MAINTENANCE_PREMISES_SELECT, input.maintenance_premises), perAnnex, annexIfSet(ctx, input.maintenance_premises_annex, 'maintenance_premises_annex'), 'maintenance_premises'),
                ]), sub('underhall.gemensamma', 'Avseende allmänna och gemensamma utrymmen', [
                    clauseChoice(ctx, MAINTENANCE_COMMON_CLAUSES, optionValue(MAINTENANCE_COMMON_SELECT, input.maintenance_common), 'Annan överenskommelse enligt bilaga', annexIfSet(ctx, input.maintenance_common_annex, 'maintenance_common_annex'), 'maintenance_common'),
                ]), shown(ctx, !!input.ancillary_spaces_maintenance)
                    ? sub('underhall.biutrymmen', 'Avseende tillhörande biutrymmen', [
                        clauseChoice(ctx, MAINTENANCE_ANCILLARY_CLAUSES, optionValue(MAINTENANCE_ANCILLARY_SELECT, input.maintenance_ancillary), perAnnex, annexIfSet(ctx, input.maintenance_ancillary_annex, 'maintenance_ancillary_annex'), 'maintenance_ancillary'),
                    ])
                    : null, sub('underhall.pafoljd', 'Påföljd vid hyresgästs åsidosättande av underhållsskyldighet', [
                    p('Om hyresgästen åsidosätter sin underhållsskyldighet enligt ovan och inte inom skälig tid vidtar rättelse efter skriftlig uppmaning har hyresvärden rätt att fullgöra skyldigheten på hyresgästens bekostnad.'),
                ]))),
            ],
        };
    },
};
export const ombyggnad = {
    ...LEGAL,
    blockKey: 'lokalhyra.ombyggnad',
    version: 'v1',
    title: 'Ombyggnads- och ändringsarbeten',
    summary: 'Alterations by the tenant: consent, landlord oversight, optional building-material declarations, public-law fees, and rent reduction during maintenance (alternatives A–C).',
    slots: [
        {
            key: 'byggvarudeklaration_included',
            label: 'Inkludera klausul om byggvarudeklaration',
            type: 'boolean',
            help: 'Valbar. Markera om hyresgästen ska förete byggvarudeklarationer i god tid före arbete.',
        },
        selectSlot('sedvanligt_underhall_alt', 'Nedsättning av hyra vid sedvanligt underhåll', SEDVANLIGT_UNDERHALL_OPTIONS, { required: true, default: 'no_reduction' }),
        attachmentSlot('sedvanligt_underhall_annex', 'Bilaga – nedsättning vid sedvanligt underhåll', {
            required: true,
            visibleWhen: whenOption('sedvanligt_underhall_alt', optionOf(SEDVANLIGT_UNDERHALL_OPTIONS, 'per_annex')),
            help: 'Välj eller skapa bilagan som reglerar rätten till nedsättning.',
        }),
    ],
    compile: (input, ctx) => ({
        attachments: contextAttachments(ctx),
        nodes: [
            section('ombyggnad', 'Ombyggnads- och ändringsarbeten', nodes(sub('ombyggnad.ratt', 'Hyresgästens rätt att utföra ombyggnads- eller ändringsarbeten', [
                p('Hyresgästen får inte, utan hyresvärdens skriftliga medgivande, utföra ombyggnads- eller ändringsarbeten. Vid hyresförhållandets upphörande ska hyresgästen återställa lokalen i godtagbart skick.'),
            ]), sub('ombyggnad.insyn', 'Hyresvärdens insyn under byggtid', [
                p('Hyresgästen ska bereda Hyresvärden insyn i ombyggnadsprojekt, bjuda in Hyresvärden till byggmöten och, när Hyresvärden begär det, ge Hyresvärden tillträde till Lokalen under ombyggnadstiden.'),
                p('Hyresgästen svarar för att arbeten i Lokalen inte skadar byggnaden eller stör den verksamhet som andra hyresgäster i byggnaden bedriver och är skyldig att i skälig omfattning ersätta Hyresvärden om krav på nedsättning av hyra riktas mot denne med anledning av hinder eller men i annans nyttjanderätt.'),
                p('Om parterna genomför en ombyggnation eller omdisponering av Lokalen ska nya ritningar upprättas. Kostnaden för upprättandet bäras av den part som påkallat förändringen.'),
            ]), shown(ctx, !!input.byggvarudeklaration_included)
                ? sub('ombyggnad.byggvarudeklaration', 'Byggvarudeklaration', [
                    p('Om Hyresgästen utför arbeten avseende Lokalen ska Hyresgästen för Hyresvärden i god tid före arbetets utförande förete byggvarudeklarationer – i den mån sådana finns utarbetade – för de produkter och material som ska tillföras Lokalen.'),
                ])
                : null, sub('ombyggnad.avgifter', 'Offentligrättsliga avgifter vid olovliga ändringar', [
                p('Om Hyresgästen utan erforderligt bygglov, annat lov eller tillstånd vidtar ändringar avseende Lokalen och Hyresvärden till följd av detta enligt reglerna i vid var tid gällande regler i plan- och bygglagen (PBL) eller annan lag som ersätter denna, tvingas utge byggsanktionsavgift eller vite, ska Hyresgästen till Hyresvärden betala ersättning med motsvarande belopp.'),
            ]), sub('ombyggnad.nedsattning', 'Nedsättning av hyra vid sedvanligt underhåll', [
                sub('ombyggnad.nedsattning.avtalat-skick', 'Avtalat skick m.m.', [
                    p('Hyresgästen har inte rätt till nedsättning av hyran för tid då Hyresvärden låter utföra arbete för att sätta Lokalen i avtalat skick eller annat arbete som särskilt anges i detta avtal med tillhörande bilagor.'),
                ]),
                sub('ombyggnad.nedsattning.sedvanligt-underhall', 'Sedvanligt underhåll', [
                    clauseChoice(ctx, SEDVANLIGT_UNDERHALL_CLAUSES, optionValue(SEDVANLIGT_UNDERHALL_OPTIONS, input.sedvanligt_underhall_alt), 'C', annexIfSet(ctx, input.sedvanligt_underhall_annex, 'sedvanligt_underhall_annex'), 'sedvanligt_underhall_alt'),
                ]),
            ], { childFormat: 'letter' }))),
        ],
    }),
};
/* ------------------------------------------------------------------ */
/* §§ Skyltning, brandskydd                                            */
/* ------------------------------------------------------------------ */
export const skyltning = {
    ...LEGAL,
    blockKey: 'lokalhyra.skyltning',
    version: 'v1',
    title: 'Skyltning',
    summary: 'Signage: consent, permits, façade works, vending machines and display cases, restoration on exit, and an optional signage programme.',
    slots: [
        {
            key: 'skyltprogram_included',
            label: 'Hyresgästen förbinder sig att följa bifogat skyltprogram',
            type: 'boolean',
            help: 'Valbar. Markera om ett skyltprogram bifogas som hyresgästen ska följa.',
        },
        attachmentSlot('skyltprogram_annex', 'Bilaga – skyltprogram', {
            required: true,
            visibleWhen: { key: 'skyltprogram_included', equals: true },
            help: 'Välj eller skapa skyltprogrammet.',
        }),
    ],
    compile: (input, ctx) => ({
        attachments: contextAttachments(ctx),
        nodes: [
            section('skyltning', 'Skyltning', nodes(p('Hyresgästen får inte utan hyresvärdens skriftliga medgivande sätta upp skylt på egen bekostnad. Hyresgästen ansvarar för tillstånd från myndighet och skyltens förenlighet med eventuellt förekommande skyltprogram.'), p('Hyresvärden äger inte rätt att vägra samtycke till sedvanlig skylt utan befogad anledning.'), p('Övriga anordningar såsom markiser och antenner får inte sättas upp utan Hyresvärdens tillstånd.'), p('Vid mera omfattande fastighetsunderhåll såsom fasadrenovering ska Hyresgästen på egen bekostnad och utan ersättning nedmontera och åter uppmontera skyltar och andra anordningar som Hyresgästen satt upp på byggnaden.'), p('Hyresvärden förbinder sig att inte sätta upp automater och skyltskåp på ytterväggarna till den av Hyresgästen förhyrda Lokalen utan Hyresgästens medgivande samt medger Hyresgästen företrädesrätt att uppsätta automater och skyltskåp på ifrågavarande väggar.'), p('Vid avflyttning ska Hyresgästen återställa husfasaden i godtagbart skick.'), shown(ctx, !!input.skyltprogram_included)
                ? optionList([
                    option(ticked(ctx, true), [
                        t('Hyresgästen förbinder sig att följa bifogat skyltprogram'),
                        ...(hasAttachment(input.skyltprogram_annex) ? [t(' ('), ...annexOrRule(ctx, input.skyltprogram_annex, 'skyltprogram_annex'), t(')')] : []),
                        t('.'),
                    ], { code: 'skyltprogram', sourceKey: 'skyltprogram_included' }),
                ], { select: 'multi' })
                : null)),
        ],
    }),
};
export const brandskydd = {
    ...LEGAL,
    blockKey: 'lokalhyra.brandskydd',
    version: 'v1',
    title: 'Brandskydd',
    summary: 'Fire protection under lagen om skydd mot olyckor: responsibility at handover, maintenance and replacement, sprinkler works and systematic fire-safety work.',
    slots: [
        selectSlot('brandskydd_initial_mode', 'Ansvar för brandskydd vid tillträdet', BRANDSKYDD_OPTIONS, { required: true, default: 'landlord' }),
        attachmentSlot('brandskydd_initial_annex', 'Bilaga – fördelning av brandskyddsansvar vid tillträde', {
            required: true,
            visibleWhen: { key: 'brandskydd_initial_mode', equals: 'per_annex' },
        }),
        attachmentSlot('brandskydd_maintenance_annex', 'Bilaga – gränsdragningslista för brandskydd', {
            help: 'Valfri — referens till gränsdragningslistan som styr ansvarsfördelningen i övrigt.',
        }),
    ],
    compile: (input, ctx) => {
        const mode = optionValue(BRANDSKYDD_OPTIONS, input.brandskydd_initial_mode);
        return {
            attachments: contextAttachments(ctx),
            nodes: [
                section('brandskydd', 'Brandskydd', [
                    p('Enligt lagen om skydd mot olyckor (2003:778) ska ägare eller nyttjanderättshavare till byggnader, lokaler eller andra anläggningar vidta de åtgärder som behövs för att förebygga brand och för att hindra eller begränsa skador till följd av brand. Åtgärderna kan vara av teknisk eller organisatorisk karaktär.'),
                    sub('brandskydd.tilltrade', 'Ansvar vid tillträdet', [
                        singleChoice([
                            option(ticked(ctx, mode === 'landlord'), 'Hyresvärden ansvarar för att Lokalen vid tillträdet uppfyller kraven på brandskydd som krävs för det avtalade användningsändamålet.', { code: 'landlord' }),
                            option(ticked(ctx, mode === 'per_annex'), [
                                t('Parterna har kommit överens om fördelningen av ansvaret för brandskydd inför tillträdet. Parternas ansvar regleras i bilaga. '),
                                ...annexOrBlank(ctx, input.brandskydd_initial_annex, 'brandskydd_initial_annex'),
                                t('.'),
                            ], { code: 'per_annex' }),
                        ], 'brandskydd_initial_mode'),
                    ]),
                    sub('brandskydd.underhall-utbyte', 'Underhåll och utbyte', [
                        p([
                            t('Hyresgästen ansvarar för underhåll och utbyte av sådant brandskydd i Lokalen som är synligt och åtkomligt för denne, såsom nödutgångsskyltar och andra brandskyddsanordningar som enbart betjänar Lokalen och som inte är styrda av byggnadens centrala system. Ansvarsfördelning i övrigt framgår av gränsdragningslistan. '),
                            ...annexOrBlank(ctx, input.brandskydd_maintenance_annex, 'brandskydd_maintenance_annex'),
                            t('.'),
                        ]),
                        p('Hyresgästen svarar för sådana brandskyddsanordningar som Hyresgästen har tillfört Lokalen.'),
                        p('Hyresgästen ansvarar för och bekostar därutöver sådant brandskydd som efter tillträdet kan komma att krävas för Lokalens nyttjande för avsedd användning.'),
                        p('Hyresgästen ska utan dröjsmål anmäla brister som denne upptäcker eller borde upptäcka i det brandskydd som Hyresvärden tillhandahåller och svarar för.'),
                    ]),
                    sub('brandskydd.sprinkler', 'Sprinklerarbeten', [
                        p('Eventuella sprinklerarbeten i Lokalen ska utföras av Hyresvärden anvisad personal. Kostnaden för sådana sprinklerarbeten bärs av den part som föranleder åtgärden.'),
                    ]),
                    sub('brandskydd.systematiskt', 'Systematiskt brandskyddsarbete', [
                        p('Parterna åtar sig att bedriva ett systematiskt brandskyddsarbete i enlighet med de allmänna råd som behörig myndighet meddelat. Vardera part är dessutom skyldig att en gång om året bereda den andre parten tillfälle att få ta del av dokumentation och uppföljning av partens systematiska brandskyddsarbete.'),
                        p('Hyresgästen förbinder sig att se till att:', { keepWithNext: true }),
                        list([
                            'räddnings- och utrymningsvägar alltid hålls öppna och inte vare sig helt eller delvis blockeras,',
                            'funktionen av sprinkler- och andra brandskyddsanordningar aldrig försämras genom t.ex. förbyggnad eller uppställning av föremål,',
                            'branddörrar hålls stängda och brandceller är intakta, och',
                            'plomberingar av handtag aldrig bryts annat än i händelse av brand eller brandövning.',
                        ]),
                    ]),
                ]),
            ],
        };
    },
};
/* ------------------------------------------------------------------ */
/* § Skatter och avgifter (Fastighetsskatteklausul.docx)               */
/* ------------------------------------------------------------------ */
export const skatterAvgifter = {
    ...LEGAL,
    blockKey: 'lokalhyra.skatter-avgifter',
    version: 'v1',
    title: 'Skatter och avgifter',
    summary: 'Property tax (included or reimbursed per annex), the tenant’s share of unforeseen public-law cost increases, and VAT on the rent.',
    slots: [
        selectSlot('property_tax_mode', 'Fastighetsskatt', PROPERTY_TAX_OPTIONS, { required: true, default: 'included' }),
        attachmentSlot('property_tax_annex', 'Bilaga – fastighetsskatteklausul', {
            required: true,
            visibleWhen: { key: 'property_tax_mode', equals: 'reimbursed_per_annex' },
            help: 'Välj eller skapa bilagan som reglerar fastighetsskattens fördelning.',
        }),
        {
            key: 'unforeseen_cost_categories',
            label: 'Vilka kategorier omfattas',
            type: 'multi_select',
            options: toSlotOptions(UNFORESEEN_OPTIONS),
            help: 'Markera de kategorier av kostnadsökningar som hyresgästen ska bära andel av. Om ingen markeras utgår klausulen helt.',
        },
        selectSlot('share_calc_mode', 'Beräkning av lokalens andel', SHARE_CALC_OPTIONS, {
            default: 'rent_based',
            visibleWhen: { key: 'unforeseen_cost_categories', includes: 'ombyggnadsatgarder' },
        }),
        {
            key: 'share_percent',
            label: 'Lokalens andel (%)',
            type: 'text',
            help: 't.ex. 12',
            visibleWhen: {
                all: [
                    { key: 'unforeseen_cost_categories', includes: 'ombyggnadsatgarder' },
                    { key: 'share_calc_mode', equals: 'percent' },
                ],
            },
        },
        selectSlot('tenant_moms_mode', 'Hyresgästens momsplikt', TENANT_MOMS_OPTIONS, { required: true, default: 'momspliktig' }),
        selectSlot('landlord_moms_mode', 'Hyresvärdens momsplikt', LANDLORD_MOMS_OPTIONS, { required: true, default: 'frivillig_aktiv' }),
    ],
    compile: (input, ctx) => {
        const cats = arr(input.unforeseen_cost_categories);
        const taxMode = optionValue(PROPERTY_TAX_OPTIONS, input.property_tax_mode);
        const shareMode = optionValue(SHARE_CALC_OPTIONS, input.share_calc_mode);
        const tenantMoms = optionValue(TENANT_MOMS_OPTIONS, input.tenant_moms_mode);
        const landlordMoms = optionValue(LANDLORD_MOMS_OPTIONS, input.landlord_moms_mode);
        return {
            attachments: contextAttachments(ctx),
            nodes: [
                section('skatter-avgifter', 'Skatter och avgifter', nodes(sub('skatter-avgifter.fastighetsskatt', 'Fastighetsskatt', [
                    singleChoice([
                        option(ticked(ctx, taxMode === 'included'), 'Fastighetsskatt ingår i hyran.', { code: 'included' }),
                        option(ticked(ctx, taxMode === 'reimbursed_per_annex'), [t('Fastighetsskatt ersätts i enlighet med bifogad fastighetsskatteklausul. '), ...annexOrBlank(ctx, input.property_tax_annex, 'property_tax_annex')], { code: 'reimbursed_per_annex' }),
                    ], 'property_tax_mode'),
                ]), shown(ctx, cats.length > 0)
                    ? sub('skatter-avgifter.oforutsedda', 'Oförutsedda kostnader – andelstal', nodes(p('Skulle efter avtalets tecknande oförutsedda kostnadsökningar uppkomma för fastigheten på grund av', { keepWithNext: true }), optionList([
                        option(ticked(ctx, cats.includes('skatt_avgift_palaga')), 'införande eller höjning av skatt, avgift eller pålaga som riksdag, regering, myndighet eller kommun beslutar om, eller', { code: 'skatt_avgift_palaga' }),
                        option(ticked(ctx, cats.includes('ombyggnadsatgarder')), 'generella ombyggnadsåtgärder eller liknande på fastigheten som hyresvärden är skyldig att utföra till följd av beslut från offentligt organ och som till någon del omfattar del av fastigheten,', { code: 'ombyggnadsatgarder' }),
                    ], { sourceKey: 'unforeseen_cost_categories', select: 'multi' }), p('ska hyresgästen med verkan från inträdd kostnadsökning betala ersättning till hyresvärden för den på lokalen belöpande andelen av den totala årliga kostnadsökningen för fastigheten.'), shown(ctx, cats.includes('ombyggnadsatgarder'))
                        ? sub('skatter-avgifter.oforutsedda.andelstal', 'Lokalens andelstal', [
                            singleChoice([
                                option(ticked(ctx, shareMode === 'percent'), [
                                    t('Lokalens andel ska under hyrestiden anses vara '),
                                    val(str(input.share_percent), { blankWidth: 'short', sourceKey: 'share_percent' }),
                                    t(' % av fastighetens totala yta.'),
                                ], { code: 'percent' }),
                                option(ticked(ctx, shareMode === 'rent_based'), 'Andelen har inte angetts. Den beräknas i stället som hyresgästens hyra (exkl. eventuell moms) i förhållande till de vid tidpunkten för kostnadsökningen utgående hyrorna i fastigheten för lokaler och bostadslägenheter. För outhyrda enheter görs en uppskattning av marknadshyra respektive bruksvärdeshyra.', { code: 'rent_based' }),
                            ], 'share_calc_mode'),
                        ])
                        : null), { childFormat: 'letter' })
                    : null, sub('skatter-avgifter.moms', 'Mervärdesskatt (moms)', [
                    sub('skatter-avgifter.moms.tenant', 'Hyresgästens momsplikt', [
                        singleChoice([
                            option(ticked(ctx, tenantMoms === 'momspliktig'), 'Hyresgästen ska bedriva momspliktig verksamhet i lokalen.', { code: 'momspliktig' }),
                            option(ticked(ctx, tenantMoms === 'ej_momspliktig'), 'Hyresgästen ska inte bedriva momspliktig verksamhet i lokalen.', { code: 'ej_momspliktig' }),
                        ], 'tenant_moms_mode'),
                    ]),
                    sub('skatter-avgifter.moms.landlord', 'Hyresvärdens momsplikt', [
                        singleChoice([
                            option(ticked(ctx, landlordMoms === 'frivillig_aktiv'), 'Fastighetsägaren/hyresvärden tillämpar frivillig beskattning för moms för uthyrningen av lokalen. Hyresgästen ska utöver hyran erlägga vid varje tillfälle gällande moms.', { code: 'frivillig_aktiv' }),
                            option(ticked(ctx, landlordMoms === 'frivillig_vid_val'), 'Om fastighetsägaren/hyresvärden väljer frivillig beskattning för moms för uthyrning av lokalen ska hyresgästen utöver hyran erlägga vid varje tillfälle gällande moms.', { code: 'frivillig_vid_val' }),
                        ], 'landlord_moms_mode'),
                    ]),
                    p('Momsen, som ska betalas samtidigt med hyran, beräknas på angivet hyresbelopp jämte, enligt vid varje tidpunkt gällande regler, på förekommande tillägg och andra ersättningar enligt avtalet.'),
                    p('Om hyresvärden till följd av hyresgästens självständiga agerande – såsom upplåtelse av lokalen helt eller delvis i andra hand, även till eget bolag, eller överlåtelse – blir skyldig att justera avdragen moms enligt mervärdesskattelagen, ska hyresgästen fullt ut ersätta hyresvärden för dennes förlorade avdragsrätt. Hyresgästen ska vidare utge ersättning för den kostnadsökning som följer av hyresvärdens förlorade avdragsrätt för ingående moms på driftskostnader som uppstår genom hyresgästens agerande.'),
                ], { childFormat: 'letter' }))),
            ],
        };
    },
};
/* ------------------------------------------------------------------ */
/* Static clauses                                                       */
/* ------------------------------------------------------------------ */
function staticSection(ref, title, summary, body) {
    return {
        ...LEGAL,
        blockKey: `lokalhyra.${ref}`,
        version: 'v1',
        title,
        summary,
        slots: [],
        compile: () => [section(ref, title, body.map((text) => p(text)))],
    };
}
export const ledningar = staticSection('ledningar', 'Ledningar för telefoni m.m.', 'The tenant pays for telephony/data cabling from the operator’s connection point.', [
    'Hyresgästen bekostar dragning av ledningar för telefoni m.m. från den anslutningspunkt operatören anger till de ställen i lokalen som hyresgästen i samråd med hyresvärden väljer.',
]);
export const myndighetsforeskrifter = staticSection('myndighetsforeskrifter', 'Myndighetsföreskrifter', 'The tenant obtains and pays for permits and authority-mandated equipment for its use of the premises.', [
    'Hyresgästen ansvarar för och bekostar alla tillstånd från myndigheter som krävs för lokalens användning för avsett ändamål. Vidare ska hyresgästen utrusta lokalen med sådan utrustning, t.ex. för brandbekämpning, som myndighet kan komma att påfordra för den verksamhet som bedrivs i lokalen.',
]);
export const utrymningsvagar = staticSection('utrymningsvagar', 'Utrymningsvägar', 'The tenant keeps escape routes clear and lets other tenants use them in emergencies.', [
    'Hyresgästen förbinder sig att inte på något sätt blockera i Lokalen belägna utrymningsvägar samt tillåter att Fastighetens övriga hyresgäster nyttjar dessa vid nödsituationer.',
]);
export const overlatelse = staticSection('overlatelse', 'Överlåtelse', 'No assignment of the lease without the landlord’s written consent.', [
    'Hyresgästen får inte utan hyresvärdens skriftliga medgivande överlåta hyresrätten eller sätta annan i sitt ställe.',
]);
export const storningar = staticSection('storningar', 'Störningar och ordning', 'No disturbing activity; the tenant answers for staff and visitors; breach permits early termination.', [
    'Hyresgästen får inte bedriva eller tillåta verksamhet i lokalen som orsakar olägenhet för fastigheten eller dess övriga hyresgäster. Hyresgästen ansvarar för att anställda, besökare och underentreprenörer följer dessa bestämmelser. Vid åsidosättande äger hyresvärden rätt att säga upp avtalet i förtid i enlighet med 12 kap. jordabalken.',
]);
export const tilltrade = staticSection('tilltrade', 'Tillträde för hyresvärden', 'Landlord access on reasonable notice, immediately in an emergency.', [
    'Hyresvärden äger rätt att efter avisering med skälig förvarning, normalt minst 48 timmar i förväg, få tillträde till lokalen för besiktning, underhållsarbeten eller visning för blivande hyresgäst. Vid akut skada eller fara äger hyresvärden omedelbart tillträde utan föregående avisering.',
]);
export const forsakring = staticSection('forsakring', 'Försäkring', 'Tenant liability/business insurance; landlord insures the building.', [
    'Hyresgästen åtar sig att under hela hyrestiden upprätthålla en allmän ansvarsförsäkring och, i förekommande fall, en företagsförsäkring som täcker skador i och på lokalen orsakade av hyresgästen eller dennes verksamhet. På begäran ska hyresgästen visa upp giltigt försäkringsbevis för hyresvärden. Hyresvärden svarar för fastighetsförsäkring avseende byggnaden.',
]);
export const konkurs = staticSection('konkurs', 'Konkurs och obestånd', 'Landlord may terminate on the tenant’s bankruptcy, reconstruction or insolvency.', [
    'Om hyresgästen försätts i konkurs äger hyresvärden rätt att säga upp hyresavtalet med omedelbar verkan, om inte konkursboet inom en månad från konkursbeslutet meddelar att det inträder i avtalet och ställer betryggande säkerhet för framtida hyresbetalningar. Detsamma gäller om hyresgästen ansöker om företagsrekonstruktion eller vid övrig obeståndslöshet som kan äventyra hyresbetalningarna.',
]);
export const forceMajeure = staticSection('force-majeure', 'Force majeure', 'Landlord excused for events beyond its control.', [
    'Hyresvärden fritar sig från skyldighet att fullgöra sin del av avtalet och från skyldighet att betala skadestånd om hyresvärdens åtaganden inte alls eller till en onormalt hög kostnad kan fullgöras på grund av krig, upplopp, arbetsinställelse, blockad, eldsvåda, explosion eller ingrepp av offentlig myndighet som hyresvärden inte råder över och inte heller kunnat förutse.',
]);
export const andringar = staticSection('andringar', 'Ändringar av avtalet', 'Amendments must be in writing.', [
    'Ändringar av avtalet ska vara skriftliga för att vara giltiga.',
]);
export const lagvalTvist = staticSection('lagval-tvist', 'Lagval och tvist', 'Swedish law; disputes go to the general courts.', [
    'Avtalet ska tolkas och tillämpas enligt svensk rätt. Tvister med anledning av avtalet ska avgöras av allmän domstol.',
]);
/* ------------------------------------------------------------------ */
/* §§ Bilagor, underskrifter                                           */
/* ------------------------------------------------------------------ */
export const bilagor = {
    ...SHARED,
    blockKey: 'lokalhyra.bilagor',
    version: 'v1',
    title: 'Bilagor',
    summary: 'The contract’s attachments, numbered by the assembler (Bilaga 1 reserved for the floor plan).',
    slots: [],
    compile: (_input, ctx) => {
        const declared = contextAttachments(ctx);
        return {
            attachments: declared,
            nodes: [
                section('bilagor', 'Bilagor', [declared.length > 0 ? attachmentList() : p([em('Inga bilagor bifogade.')])]),
            ],
        };
    },
};
export { SIGNING_STATEMENT_DIGITAL, signingStatementPhysical } from '../common/signing.js';
/**
 * Signature panels under the execution statement (physical vs digital,
 * coupled to the host's place/date toggle). Landlord and tenant always
 * sign; the medhyresgäst once any of its fields is filled, the borgensman
 * once named (always, on a blank form).
 */
export const underskrift = signingBlockDefinition({
    blockKey: 'lokalhyra.underskrift',
    summary: 'Signature panels for landlord, tenant and (when present) co-tenant and guarantor, under a physical or digital signing statement.',
    defaults: {
        ref: 'underskrift',
        title: 'Underskrifter',
        statement: true,
        parties: [
            { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
            { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
            {
                label: 'Medhyresgäst',
                role: 'lessee',
                ordinal: 1,
                visible_when: { any: COTENANT_KEYS.map((key) => ({ key, isSet: true })) },
            },
            { label: 'Borgensman', role: 'guarantor', ordinal: 0, when_set: 'guarantor_name' },
        ],
    },
});
export const LOKALHYRA_BLOCKS = [
    avtalsuppgifter,
    hyresvard,
    hyresgast,
    hyresobjekt,
    hyrestid,
    anvandning,
    skick,
    hyra,
    arligHojning,
    forbrukning,
    avfallshantering,
    bevakning,
    sakerhet,
    besittningsskydd,
    lasStold,
    underhall,
    ombyggnad,
    skyltning,
    ledningar,
    myndighetsforeskrifter,
    brandskydd,
    utrymningsvagar,
    skatterAvgifter,
    overlatelse,
    storningar,
    tilltrade,
    forsakring,
    konkurs,
    forceMajeure,
    andringar,
    lagvalTvist,
    bilagor,
    underskrift,
];
//# sourceMappingURL=blocks.js.map